/**
 * Reading the Colyseus schema without owning it.
 *
 * `packages/shared` is m3-server's; it declares `RoomSnapshot` (the shape) but
 * not the `Schema` classes, so this client decodes the room state by reflection
 * — Colyseus 0.16 ships the schema definition over the wire and builds the
 * instance for us. That is untyped by construction, so every read goes through
 * the accessors below and the whole file is the one place a cast happens.
 *
 * Everything here is total: a field the server has not implemented yet reads as
 * its neutral value rather than throwing. The table then renders without a stake
 * badge instead of not rendering at all, which is the right failure while the
 * two halves of M3 are landing independently.
 */

import type { LanceId, Phase, Seat } from '@ordago/rules';
import type { RoomConfig, RoomPhase, SeatSnapshot } from '@ordago/shared';

import type { BettingPublic, LancePublic } from '../store/types';

/** The engine phases; anything else on `state.phase` is a room phase. */
const GAME_PHASES = new Set<string>([
  'idle',
  'mus',
  'discard',
  'grande',
  'chica',
  'paresDeclare',
  'pares',
  'juegoDeclare',
  'juego',
  'punto',
  'handEnd',
  'gameOver',
]);

const LANCE_IDS = new Set<string>(['grande', 'chica', 'pares', 'juego', 'punto']);

type Raw = Record<string, unknown>;

/** The one cast. Everything downstream is checked. */
function asRaw(value: unknown): Raw {
  return (value ?? {}) as Raw;
}

function str(o: Raw, key: string, fallback = ''): string {
  const v = o[key];
  return typeof v === 'string' ? v : fallback;
}

function num(o: Raw, key: string, fallback = 0): number {
  const v = o[key];
  return typeof v === 'number' && Number.isFinite(v) ? v : fallback;
}

function bool(o: Raw, key: string, fallback = false): boolean {
  const v = o[key];
  return typeof v === 'boolean' ? v : fallback;
}

/** `-1` is the schema's "none"; it becomes `null` on this side of the wire. */
function seatOrNull(o: Raw, key: string): Seat | null {
  const v = num(o, key, -1);
  return v >= 0 && v <= 3 ? (v as Seat) : null;
}

/** The schema's `-1 unknown | 0 no | 1 yes` encoding for a declaration. */
function tristate(o: Raw, key: string): boolean | null {
  const v = num(o, key, -1);
  return v < 0 ? null : v === 1;
}

/** `ArraySchema` is iterable but is not an `Array`. */
function list(o: Raw, key: string): unknown[] {
  const v = o[key];
  if (Array.isArray(v)) return v;
  if (v !== null && typeof v === 'object' && Symbol.iterator in v) {
    return Array.from(v as Iterable<unknown>);
  }
  return [];
}

// ─────────────────────────────────────────────────────────────────── projection

export interface RoomView {
  code: string | null;
  phase: RoomPhase;
  gamePhase: Phase | null;
  lance: LanceId | null;
  config: RoomConfig;
  leaderSeat: Seat | null;
  seats: SeatSnapshot[];
  dealer: Seat | null;
  mano: Seat | null;
  turnSeat: Seat | null;
  handNumber: number;
  musRound: number;
  stockCount: number;
  scores: [number, number];
  gamesWon: [number, number];
  ranked: boolean;
  betting: BettingPublic | null;
  lances: LancePublic[];
  /**
   * Public by rule: a declaration is spoken at the table, so it is schema state
   * rather than a private message. `-1` on the wire means "not asked yet".
   */
  declaredPares: (boolean | null)[];
  declaredJuego: (boolean | null)[];
  /**
   * The server's clock, refreshed on every patch. A cruder offset source than
   * the ping median — it carries one-way latency as bias — but it is always
   * there, so a client whose pings went unanswered still renders a sane
   * countdown instead of one that is thirty seconds out.
   */
  serverNow: number;
  /** Non-empty only between the reveal and the next deal. */
  revealedBySeat: Partial<Record<Seat, string[]>>;
}

function projectConfig(raw: Raw): RoomConfig {
  const c = asRaw(raw['config']);
  const variant = str(c, 'variant', '8r');
  const senasMode = str(c, 'senasMode', 'private');
  const paceTier = str(c, 'paceTier', 'normal');
  return {
    name: str(raw, 'roomName'),
    isPrivate: bool(raw, 'isPrivate'),
    variant: variant === '4r' ? '4r' : '8r',
    targetPoints: num(c, 'targetPoints', 40) === 30 ? 30 : 40,
    vaca: bool(c, 'vaca'),
    vacaBestOf: num(c, 'vacaBestOf', 3) === 5 ? 5 : 3,
    paceTier:
      (['sinReloj', 'tranquilo', 'normal', 'rapido', 'muyRapido'] as const).find(
        (t) => t === paceTier,
      ) ?? 'normal',
    senasMode: senasMode === 'off' || senasMode === 'catchable' ? senasMode : 'private',
    senasCatchPct: num(c, 'senasCatchPct', 25),
    botsAllowed: bool(c, 'botsAllowed'),
  };
}

function projectSeat(raw: unknown, index: number): SeatSnapshot {
  const s = asRaw(raw);
  const userId = str(s, 'userId');
  const botKind = str(s, 'botKind');
  return {
    seat: num(s, 'seat', index) as Seat satisfies Seat,
    userId: userId === '' ? null : userId,
    username: str(s, 'username'),
    avatarUrl: str(s, 'avatarUrl'),
    connected: bool(s, 'connected'),
    isBot: bool(s, 'isBot'),
    botKind: botKind === 'fill' || botKind === 'autopilot' ? botKind : null,
    ready: bool(s, 'ready'),
    cardCount: num(s, 'cardCount'),
    lastDiscardCount: num(s, 'lastDiscardCount'),
    clock: {
      deadlineAt: num(asRaw(s['clock']), 'deadlineAt'),
      reserveMs: num(asRaw(s['clock']), 'reserveMs'),
    },
  };
}

function projectBetting(raw: Raw): BettingPublic | null {
  const b = asRaw(raw['betting']);
  const lance = str(b, 'lance');
  if (!LANCE_IDS.has(lance)) return null;
  const currentTeam = num(b, 'currentTeam', -1);
  return {
    lance: lance as LanceId,
    participants: list(b, 'participants')
      .filter((v): v is number => typeof v === 'number')
      .map((v) => v as Seat),
    turn: seatOrNull(b, 'turn'),
    currentAmount: num(b, 'currentAmount'),
    currentTeam: currentTeam === 0 || currentTeam === 1 ? currentTeam : null,
    ordago: bool(b, 'ordago'),
    previousStake: num(b, 'previousStake'),
  };
}

function projectLances(raw: Raw): LancePublic[] {
  return list(raw, 'lances').flatMap((entry): LancePublic[] => {
    const l = asRaw(entry);
    const lance = str(l, 'lance');
    if (!LANCE_IDS.has(lance)) return [];
    const winner = num(l, 'winner', -1);
    return [
      {
        lance: lance as LanceId,
        status: str(l, 'status'),
        reason: str(l, 'reason'),
        stake: num(l, 'stake'),
        bonus: num(l, 'bonus'),
        winner: winner === 0 || winner === 1 ? winner : null,
      },
    ];
  });
}

/**
 * The room lifecycle and the engine's phase are two separate schema fields —
 * `phase` is `lobby | playing | finished`, `gamePhase` is the engine's — which
 * is what `RoomSnapshot` describes. (backend.md §3.2 sketches them merged into
 * one string instead; the implemented schema follows `RoomSnapshot`, and reading
 * only `phase` left the table stuck on the pre-game screen while the server was
 * already dealing.) The merged form is still accepted, because a single field
 * carrying an engine phase is unambiguous.
 */
function splitPhase(
  value: string,
  gamePhaseValue: string,
  winnerTeam: number,
): { phase: RoomPhase; gamePhase: Phase | null } {
  const gamePhase = GAME_PHASES.has(gamePhaseValue) ? (gamePhaseValue as Phase) : null;

  if (value === 'playing') return { phase: 'playing', gamePhase };
  if (value === 'finished') return { phase: 'finished', gamePhase };
  if (value === 'lobby') return { phase: 'lobby', gamePhase };

  // The merged form: `phase` is itself an engine phase.
  if (GAME_PHASES.has(value)) {
    const merged = value as Phase;
    const finished = merged === 'gameOver' && winnerTeam >= 0;
    return { phase: finished ? 'finished' : 'playing', gamePhase: merged };
  }
  return { phase: 'lobby', gamePhase };
}

export function projectState(state: unknown): RoomView {
  const raw = asRaw(state);
  const code = str(raw, 'code');
  const seats = list(raw, 'seats').map(projectSeat);
  const { phase, gamePhase } = splitPhase(
    str(raw, 'phase', 'lobby'),
    str(raw, 'gamePhase'),
    num(raw, 'winnerTeam', -1),
  );

  const revealedBySeat: Partial<Record<Seat, string[]>> = {};
  const declaredPares: (boolean | null)[] = [null, null, null, null];
  const declaredJuego: (boolean | null)[] = [null, null, null, null];
  for (const [i, entry] of list(raw, 'seats').entries()) {
    const seat = asRaw(entry);
    const cards = list(seat, 'revealedCards').filter((c): c is string => typeof c === 'string');
    if (cards.length > 0) revealedBySeat[i as Seat] = cards;
    declaredPares[i] = tristate(seat, 'declaredPares');
    declaredJuego[i] = tristate(seat, 'declaredJuego');
  }

  const betting = projectBetting(raw);

  return {
    code: code === '' ? null : code,
    phase,
    gamePhase,
    lance: betting?.lance ?? null,
    config: projectConfig(raw),
    leaderSeat: seatOrNull(raw, 'leaderSeat'),
    seats,
    dealer: seatOrNull(raw, 'dealer'),
    mano: seatOrNull(raw, 'mano'),
    turnSeat: seatOrNull(raw, 'turn'),
    handNumber: num(raw, 'handNumber'),
    musRound: num(raw, 'musRound'),
    stockCount: num(raw, 'stockCount'),
    scores: [num(raw, 'scoreT0'), num(raw, 'scoreT1')],
    gamesWon: [num(raw, 'gamesWonT0'), num(raw, 'gamesWonT1')],
    ranked: bool(asRaw(raw['config']), 'ranked', true),
    betting,
    lances: projectLances(raw),
    declaredPares,
    declaredJuego,
    serverNow: num(raw, 'serverNow'),
    revealedBySeat,
  };
}

// ────────────────────────────────────────────────────────── identity preservation

function sameSeat(a: SeatSnapshot, b: SeatSnapshot): boolean {
  return (
    a.seat === b.seat &&
    a.userId === b.userId &&
    a.username === b.username &&
    a.avatarUrl === b.avatarUrl &&
    a.connected === b.connected &&
    a.isBot === b.isBot &&
    a.botKind === b.botKind &&
    a.ready === b.ready &&
    a.cardCount === b.cardCount &&
    a.lastDiscardCount === b.lastDiscardCount &&
    a.clock.deadlineAt === b.clock.deadlineAt &&
    a.clock.reserveMs === b.clock.reserveMs
  );
}

/**
 * Keeps the previous object for every seat that did not actually change, so a
 * selector on one seat does not re-render the other three. This is the reason
 * frontend.md §1.2 chose Zustand over Context, and it only works if the
 * projection preserves identity — a fresh array of fresh objects every patch
 * would make the selectors useless.
 */
export function reuseSeats(previous: SeatSnapshot[], next: SeatSnapshot[]): SeatSnapshot[] {
  if (previous.length !== next.length) return next;
  let changed = false;
  const merged = next.map((seat, i) => {
    const before = previous[i];
    if (before !== undefined && sameSeat(before, seat)) return before;
    changed = true;
    return seat;
  });
  return changed ? merged : previous;
}

export function sameLances(a: LancePublic[], b: LancePublic[]): boolean {
  if (a.length !== b.length) return false;
  return a.every((x, i) => {
    const y = b[i];
    return (
      y !== undefined &&
      x.lance === y.lance &&
      x.status === y.status &&
      x.reason === y.reason &&
      x.stake === y.stake &&
      x.bonus === y.bonus &&
      x.winner === y.winner
    );
  });
}

export function sameBetting(a: BettingPublic | null, b: BettingPublic | null): boolean {
  if (a === null || b === null) return a === b;
  return (
    a.lance === b.lance &&
    a.turn === b.turn &&
    a.currentAmount === b.currentAmount &&
    a.currentTeam === b.currentTeam &&
    a.ordago === b.ordago &&
    a.previousStake === b.previousStake &&
    a.participants.length === b.participants.length &&
    a.participants.every((p, i) => p === b.participants[i])
  );
}
